/**
 * Website-template multi-media tile (Home featured + Content catalog).
 * Ant Carousel + CreatorFeedVideoPlayer, same as search PostCarousel.
 * Dots are drawn inside this tile (not slick), so LiB / search slick UI is unchanged.
 */

import React, { useEffect, useRef, useState } from "react";
import { Carousel } from "antd";
import type { CarouselRef } from "antd/es/carousel";
import { CreatorFeedVideoPlayer } from "../../../components/FeedPost/PlatformPosts/CreatorFeedVideoPlayer";
import { CatalogEagerMediaContext, FadeInImage } from "./TemplateRevealImage";
import { getBudgetedTemplateImageSrc } from "../../../Utils/templateImageDelivery";
import { isTemplateCarouselSlideVideo } from "./templatePostMedia";

type TemplatePostMediaCarouselProps = {
  slides: string[];
  posters?: string[];
  fallbackPoster?: string;
  imageClassName: string;
  videoClassName: string;
  stillOnly?: boolean;
  allowVideo?: boolean;
  isSoundOn?: boolean;
  hideHoverSound?: boolean;
  hidePlayButton?: boolean;
  muted?: boolean;
  onMutedChange?: (muted: boolean) => void;
  focused?: boolean;
  playableVideoUrl?: string;
  onSlideChange?: (index: number) => void;
};

export function TemplatePostMediaCarousel({
  slides,
  posters = [],
  fallbackPoster = "/assets/dummy_video.png",
  imageClassName,
  videoClassName,
  stillOnly = false,
  allowVideo = false,
  isSoundOn = true,
  hideHoverSound,
  hidePlayButton,
  muted,
  onMutedChange,
  focused,
  playableVideoUrl,
  onSlideChange,
}: TemplatePostMediaCarouselProps) {
  const eagerCatalog = React.useContext(CatalogEagerMediaContext);
  const carouselRef = useRef<CarouselRef>(null);
  const [activeSlide, setActiveSlide] = useState(0);
  const [finePointer, setFinePointer] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(hover: hover) and (pointer: fine)").matches,
  );
  useEffect(() => {
    const mq = window.matchMedia("(hover: hover) and (pointer: fine)");
    const apply = () => setFinePointer(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);
  if (slides.length < 2) return null;

  const dots = (
    <div
      className="mte-tpl-post-media-dots"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      {slides.map((_, index) => (
        <button
          key={index}
          type="button"
          aria-label={`Slide ${index + 1}`}
          className={
            index === activeSlide
              ? "mte-tpl-post-media-dot is-active"
              : "mte-tpl-post-media-dot"
          }
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setActiveSlide(index);
            carouselRef.current?.goTo(index);
          }}
        />
      ))}
    </div>
  );

  // Desktop Content/Search: slick's translate layer on every carousel blanks
  // the page during a fast scroll. Keep the current still until hover.
  if (eagerCatalog && finePointer && !focused) {
    const slideIndex = Math.min(activeSlide, slides.length - 1);
    const url = slides[slideIndex];
    const isVideo = isTemplateCarouselSlideVideo(url, playableVideoUrl);
    const poster = posters[slideIndex] || fallbackPoster;
    return (
      <div className="mte-tpl-post-media-carousel">
        <div className="mte-tpl-post-media-slide">
          <FadeInImage
            src={
              isVideo ? poster : getBudgetedTemplateImageSrc(url, "shop")
            }
            alt=""
            className={imageClassName}
            draggable={false}
            loading="eager"
            decoding="async"
          />
        </div>
        {dots}
      </div>
    );
  }

  return (
    <div className="mte-tpl-post-media-carousel">
      <Carousel
        ref={carouselRef}
        dots={false}
        infinite
        speed={200}
        draggable
        waitForAnimate={false}
        initialSlide={activeSlide}
        afterChange={(current) => {
          setActiveSlide(current);
          onSlideChange?.(current);
        }}
      >
        {slides.map((url, index) => {
          const isVideo = isTemplateCarouselSlideVideo(url, playableVideoUrl);
          const poster = posters[index] || fallbackPoster;
          const playVideo =
            !stillOnly && isVideo && index === activeSlide && allowVideo;
          return (
            <div
              key={`${index}-${url.slice(-48)}`}
              className="mte-tpl-post-media-slide"
            >
              {playVideo ? (
                <CreatorFeedVideoPlayer
                  media_url={url}
                  thumbnail={poster}
                  isSoundOn={isSoundOn}
                  hideHoverSound={hideHoverSound}
                  hidePlayButton={hidePlayButton}
                  muted={muted}
                  onMutedChange={onMutedChange}
                  focused={focused}
                  className={videoClassName}
                  style={{ width: "100%", height: "100%" }}
                />
              ) : (
                <FadeInImage
                  src={
                    isVideo
                      ? poster
                      : eagerCatalog
                        ? getBudgetedTemplateImageSrc(url, "shop")
                        : url
                  }
                  alt=""
                  className={imageClassName}
                  draggable={false}
                  {...(eagerCatalog
                    ? { loading: "eager" as const, decoding: "async" as const }
                    : {})}
                />
              )}
            </div>
          );
        })}
      </Carousel>
      {dots}
    </div>
  );
}
